import { ValueChangeEvent } from '@swivel-finance/ui/elements/input';
import { ListBoxElement } from '@swivel-finance/ui/elements/listbox';
import { PanelDirection, PanelNavigationEvent } from '@swivel-finance/ui/elements/panel-container';
import { SelectElement } from '@swivel-finance/ui/elements/select/select';
import { TooltipElement } from '@swivel-finance/ui/elements/tooltip/tooltip';
import { dispatch } from '@swivel-finance/ui/utils/events';
import { LitElement, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { ERRORS, NOTIFICATIONS, TOOLTIPS } from '../../core/constants';
import { marketKey } from '../../core/markets';
import { notifications } from '../../services/notification';
import { balance, errorMessage, maturityBar, rate, status, tokenImage, tokenSymbol } from '../../shared/templates';
import { ACCOUNT, MARKET, orchestrator } from '../../state/orchestrator';
import { Market, Token } from '../../types';

const template = function (this: LendMarketElement) {

    const isConnected = this.accountMachine.state.matches(ACCOUNT.STATES.CONNECTED);
    const { balances } = this.accountMachine.state.context;

    const state = this.marketMachine.state;
    const context = state.context;

    const isFetchingQuotes = state.matches(MARKET.STATES.FETCHING_QUOTES);
    const isFetching = state.matches(MARKET.STATES.FETCHING);
    const isValid = state.matches(MARKET.STATES.SELECT_MARKET) || state.matches(MARKET.STATES.COMPLETE) || isFetchingQuotes || isFetching;

    const { tokens, selectedToken, quotesByMarket, quotesByToken, activeMarketsByAPR, activeTokensByAPR } = context;

    const token = selectedToken ? tokens.get(selectedToken) : undefined;

    const hasQuotes = isFetching || isFetchingQuotes || quotesByMarket.size > 0;

    return html`
    <div class="widget">

        <div class="widget-header">
            <button class="button-icon" @click=${ () => dispatch(this, new PanelNavigationEvent({ target: this, panel: PanelDirection.PREVIOUS })) }>
                <ui-icon name="back-circle"></ui-icon>
            </button>
            <label id="maturities-label" aria-describedby="lend-select-maturity-header-tooltip">Select a Maturity</label>
            <ui-tooltip id="lend-select-maturity-header-tooltip">
                ${ TOOLTIPS.LEND.SELECT_MATURITY.HEADER() }
            </ui-tooltip>
        </div>

        <div class="widget-inputs">
            <ui-select
                @ui-value-changed=${ (event: ValueChangeEvent<Token>) => this.handleTokenChange(event) }
                ${ ref(this.selectRef) }>
                <button class="ui-select-trigger" data-part="trigger">
                    <span class="ui-select-trigger-label">
                        <ill-token-symbol .name=${ (this.selectRef.value?.value as Token)?.image ?? token?.image }></ill-token-symbol>
                        ${ (this.selectRef.value?.value as Token)?.symbol ?? token?.symbol }
                    </span>
                    <ui-icon class="ui-select-trigger-toggle" name="triangle"></ui-icon>
                </button>
                <ui-listbox class="tokens-listbox" data-part="overlay">
                    ${ tokens
                        ? activeTokensByAPR.map(token => html`
                        <ui-listitem .value=${ token } aria-selected="${ token.address === selectedToken }" aria-disabled="${ !quotesByToken.has(token.address) }">
                            <ill-token-symbol .name=${ token.image }></ill-token-symbol>
                            ${ token.symbol }
                            <span class="account-balance ${ isConnected ? 'connected' : '' }">
                                ${ isConnected
                                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                    ? balances.has(token.address) && balance(balances.get(token.address)!.balance, token, 2) || status('loading')
                                    : html`Connect to see balances.`
                                }
                            </span>
                        </ui-listitem>
                        `)
                        : nothing
                    }
                </ui-listbox>
            </ui-select>
        </div>

        <div class="widget-main">

            ${ isValid && hasQuotes
                ? html`
                <ui-tooltip id="lend-market-rate-preview-tooltip">
                    ${ TOOLTIPS.LEND.SELECT_MATURITY.BEST_RATE(token) }
                </ui-tooltip>

                <ui-tooltip id="lend-market-paused-tooltip">
                    <p>${ TOOLTIPS.PAUSED.QUOTES() }</p>
                    <p>${ TOOLTIPS.PAUSED.MORE_INFO() }</p>
                </ui-tooltip>

                <ui-listbox
                    class="neo-list market-list"
                    aria-labelledby="maturities-label"
                    @ui-value-changed=${ (event: ValueChangeEvent<Market>) => this.handleMarketChange(event) }
                    ${ ref(this.listboxRef) }>
                        ${ activeMarketsByAPR.map(market => html`
                        <ui-listitem class="neo-item" .value=${ market } aria-disabled="${ !quotesByMarket.has(marketKey(market)) }">
                            <div class="neo-item-content">
                                <span class="token">
                                    ${ tokenImage(market.token) }
                                    ${ tokenSymbol(market.token) }
                                </span>
                                ${ maturityBar(market) }
                                <!-- NB: we need to manually set the tabindex to "-1" as the tooltip will add a
                                    tabindex of "0" to tooltip triggers by default (for accessibility)
                                    however, these tooltips are children of a focusable list item and, as such,
                                    they shouldn't be focusable -->
                                ${ !quotesByMarket.has(marketKey(market))
                                    ? html`
                                    <ui-icon name="pause" aria-describedby="lend-market-paused-tooltip" tabindex="-1"></ui-icon>
                                    `
                                    : html`
                                    <span class="rate" aria-describedby="lend-market-rate-preview-tooltip" tabindex="-1">
                                        ${ isFetchingQuotes
                                            ? status('loading')
                                            : rate(quotesByMarket.get(marketKey(market))?.apr)
                                        }
                                    </span>
                                    `
                                }
                            </div>
                        </ui-listitem>
                        `) }
                </ui-listbox>
                `
                : state.matches(MARKET.STATES.FETCHING)
                    ? status('loading')
                    : state.matches(MARKET.STATES.ERROR) || !hasQuotes
                        ? html`
                        ${ errorMessage(state.context.error ?? ERRORS.STATE.MARKET.NO_QUOTES.message, 'exclamation') }
                        <button class="primary" @click=${ () => this.retry() }>Retry</button>
                        `
                        : nothing
            }

        </div>

    </div>
    `;
};

@customElement('ill-lend-market')
export class LendMarketElement extends LitElement {

    protected accountMachine = orchestrator.account;

    protected marketMachine = orchestrator.market;

    protected selectRef = createRef<SelectElement>();

    protected listboxRef = createRef<ListBoxElement>();

    constructor () {

        super();

        this.handleAccountTransition = this.handleAccountTransition.bind(this);
        this.handleMarketTransition = this.handleMarketTransition.bind(this);
    }

    connectedCallback (): void {

        super.connectedCallback();

        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.accountMachine.onTransition(this.handleAccountTransition);
        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.marketMachine.onTransition(this.handleMarketTransition);
    }

    disconnectedCallback (): void {

        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.accountMachine.off(this.handleAccountTransition);
        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.marketMachine.off(this.handleMarketTransition);

        super.disconnectedCallback();
    }

    protected createRenderRoot (): Element | ShadowRoot {
        return this;
    }

    protected render (): unknown {

        return template.apply(this);
    }

    protected updated (): void {

        const tooltips = this.renderRoot.querySelectorAll<TooltipElement>('ui-tooltip');

        tooltips.forEach(tooltip => tooltip.connectTooltip());
    }

    protected handleAccountTransition (): void {

        this.requestUpdate();
    }

    protected handleMarketTransition (state: typeof orchestrator.market.state): void {

        // when entering the seletc market state we want to reset the market listbox
        if (state.matches(MARKET.STATES.SELECT_MARKET)) {

            this.listboxRef.value?.reset();
        }

        this.requestUpdate();
    }

    protected handleTokenChange (event: ValueChangeEvent<Token>): void {

        const { current, change } = event.detail;

        if (!change) return;

        // we wait for the token select to close before sending the event the state machine
        this.selectRef.value?.addEventListener('ui-open-changed', () => {

            this.marketMachine.send(MARKET.model.events[MARKET.EVENTS.SET_TOKEN](current.address));
            this.listboxRef.value?.reset();

        }, { once: true });

        this.requestUpdate();
    }

    protected handleMarketChange (event: ValueChangeEvent<Market>): void {

        const { current, change } = event.detail;

        if (!change) return;

        if (!this.accountMachine.state.matches(ACCOUNT.STATES.CONNECTED)) {

            notifications.show({
                type: 'info',
                content: NOTIFICATIONS.LEND.CONNECT,
            });

            this.listboxRef.value?.reset();

            return;
        }

        this.marketMachine.send(MARKET.model.events[MARKET.EVENTS.SET_MARKET](marketKey(current)));
    }

    protected retry (): void {

        this.marketMachine.send(MARKET.model.events[MARKET.EVENTS.FETCH_QUOTES]());
    }
}
